// frontend/src/utils/reference/buildEntityState.js
// Owns: entity candidate admission, group-target extraction, and pre-arbitration entity-state assembly

import {
  createEmptyEntityState,
  createEntityCandidate,
  dedupeEntities,
  sameEntity,
  sortEntitiesByScore,
  toHistoricalEntities,
} from './entityStateHelpers';
import { arbitrateEntityState } from './entityArbitrator';

function normalizeText(value = '') {
  return String(value || '').toLowerCase().trim();
}

function normalizeWhitespace(value = '') {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function escapeRegExp(value = '') {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getRelationshipRole(person = {}) {
  return normalizeText(
    person.relationship ||
      person.relationship_type ||
      person.relationshipType ||
      ''
  );
}

const ROLE_ALIASES = {
  daughter: ['daughter', 'daughters', 'girl', 'girls'],
  son: ['son', 'sons', 'boy', 'boys'],
  child: ['child'],
  friend: ['friend', 'friends', 'buddy', 'buddies', 'pal', 'pals'],
  brother: ['brother', 'brothers', 'bro'],
  sister: ['sister', 'sisters', 'sis'],
  mother: ['mother', 'mothers', 'mom', 'moms', 'mama', 'momma', 'mommy'],
  father: ['father', 'fathers', 'dad', 'dads', 'papa', 'pop', 'daddy'],
  grandmother: ['grandmother', 'grandmothers', 'grandma', 'grandmas', 'nana', 'granny'],
  grandfather: ['grandfather', 'grandfathers', 'grandpa', 'grandpas', 'gramps', 'pop-pop'],
  aunt: ['aunt', 'aunts'],
  uncle: ['uncle', 'uncles'],
  cousin: ['cousin', 'cousins'],
  dog: ['dog', 'dogs', 'puppy', 'puppies', 'pupper'],
  cat: ['cat', 'cats', 'kitty', 'kitties', 'kitten', 'kittens'],
  pet: ['pet', 'pets'],
  boyfriend: ['boyfriend', 'boyfriends'],
  girlfriend: ['girlfriend', 'girlfriends'],
  fiance: ['fiance', 'fiances', 'fiancé', 'fiancés'],
  fiancee: ['fiancee', 'fiancees', 'fiancée', 'fiancées'],
  wife: ['wife', 'wives', 'spouse'],
  husband: ['husband', 'husbands', 'spouse'],
  'ex-wife': ['ex wife', 'ex wives', 'ex-wife', 'ex-wives'],
  'ex-husband': ['ex husband', 'ex husbands', 'ex-husband', 'ex-husbands'],
};

const GROUP_BUCKET_ALIASES = {
  children: ['kid', 'kids', 'child', 'children'],
  parents: ['parent', 'parents'],
  siblings: ['sibling', 'siblings'],
  grandparents: ['grandparent', 'grandparents'],
  friends: ['friend', 'friends', 'buddy', 'buddies', 'pal', 'pals'],
  pets: ['pet', 'pets', 'dog', 'dogs', 'cat', 'cats'],
  family: ['family'],
};

const GROUP_BUCKET_MEMBERS = {
  children: ['son', 'daughter', 'child'],
  parents: ['mother', 'father'],
  siblings: ['brother', 'sister'],
  grandparents: ['grandmother', 'grandfather'],
  friends: ['friend'],
  pets: ['dog', 'cat', 'pet'],
  family: [
    'mother',
    'father',
    'brother',
    'sister',
    'son',
    'daughter',
    'child',
    'grandmother',
    'grandfather',
    'dog',
    'cat',
    'pet',
  ],
};

const GROUP_DISPLAY_NAMES = {
  children: { singular: 'child', plural: 'children' },
  parents: { singular: 'parent', plural: 'parents' },
  siblings: { singular: 'sibling', plural: 'siblings' },
  grandparents: { singular: 'grandparent', plural: 'grandparents' },
  friends: { singular: 'friend', plural: 'friends' },
  pets: { singular: 'pet', plural: 'pets' },
  family: { singular: 'family member', plural: 'family' },
};

function getCanonicalRole(role = '') {
  const normalizedRole = normalizeText(role);
  if (!normalizedRole) return '';

  for (const [canonicalRole, aliases] of Object.entries(ROLE_ALIASES)) {
    if (canonicalRole === normalizedRole || aliases.includes(normalizedRole)) {
      return canonicalRole;
    }
  }

  return normalizedRole;
}

function getBucketForAlias(alias = '') {
  const normalizedAlias = normalizeText(alias);
  if (!normalizedAlias) return '';

  for (const [bucket, aliases] of Object.entries(GROUP_BUCKET_ALIASES)) {
    if (bucket === normalizedAlias || aliases.includes(normalizedAlias)) {
      return bucket;
    }
  }

  return '';
}

function getBucketsForRole(role = '') {
  const canonicalRole = getCanonicalRole(role);
  if (!canonicalRole) return [];

  return Object.entries(GROUP_BUCKET_MEMBERS)
    .filter(([, members]) => members.includes(canonicalRole))
    .map(([bucket]) => bucket);
}

function getGroupDisplayNameForBucket(bucket = '', count = 0) {
  const normalizedBucket = normalizeText(bucket);
  const label = GROUP_DISPLAY_NAMES[normalizedBucket];

  if (!label) {
    return count === 1 ? normalizedBucket : `${normalizedBucket}s`;
  }

  return count === 1 ? label.singular : label.plural;
}

function messageMentionsAlias(message = '', alias = '') {
  const lower = normalizeText(message);
  const normalizedAlias = normalizeText(alias);

  if (!lower || !normalizedAlias) return false;

  const escaped = escapeRegExp(normalizedAlias);
  const pattern = new RegExp(`\\b${escaped}\\b`, 'i');
  return pattern.test(lower);
}

function getEntityAliasList(person = {}) {
  const aliasSources = [
    person.nickname,
    person.nicknames,
    person.alias,
    person.aliases,
    person.alternateName,
    person.alternateNames,
    person.alternate_name,
    person.alternate_names,
    person.otherNames,
    person.other_names,
    person.metadata?.nickname,
    person.metadata?.nicknames,
    person.metadata?.alias,
    person.metadata?.aliases,
    person.metadata?.alternateName,
    person.metadata?.alternateNames,
    person.metadata?.alternate_name,
    person.metadata?.alternate_names,
  ];

  const flattened = aliasSources.flatMap((value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return value.includes(',')
        ? value.split(',').map((part) => part.trim())
        : [value];
    }
    return [];
  });

  return [...new Set(flattened.map(normalizeWhitespace).filter(Boolean))];
}

function buildPersonNameVariants(person = {}) {
  const firstName = normalizeWhitespace(person.first_name || person.firstName || '');
  const lastName = normalizeWhitespace(person.last_name || person.lastName || '');
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return [...new Set([firstName, fullName, ...getEntityAliasList(person)].filter(Boolean))];
}

function findExplicitRelationshipMatches(message = '', relationships = []) {
  const lower = normalizeText(message);

  if (!lower || !Array.isArray(relationships) || relationships.length === 0) {
    return [];
  }

  const fullNameMatches = relationships.filter((person) => {
    const firstName = normalizeWhitespace(person.first_name || person.firstName || '');
    const lastName = normalizeWhitespace(person.last_name || person.lastName || '');
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

    return fullName && messageMentionsAlias(lower, fullName);
  });

  if (fullNameMatches.length > 0) {
    return fullNameMatches
      .map((person) =>
        createEntityCandidate(person, {
          source: 'explicit_full_name_match',
          signals: {
            explicitlyNamed: true,
          },
          metadata: {
            matchedBy: 'full_name',
            aliases: getEntityAliasList(person),
            buckets: getBucketsForRole(getRelationshipRole(person)),
          },
        })
      )
      .filter(Boolean);
  }

  const directNameOrAliasMatches = relationships.filter((person) => {
    const variants = buildPersonNameVariants(person);
    return variants.some((variant) => messageMentionsAlias(lower, variant));
  });

  return directNameOrAliasMatches
    .map((person) => {
      const variants = buildPersonNameVariants(person);
      const matchedVariant =
        variants.find((variant) => messageMentionsAlias(lower, variant)) || null;

      return createEntityCandidate(person, {
        source:
          matchedVariant &&
          matchedVariant !== normalizeWhitespace(person.first_name || person.firstName || '')
            ? 'explicit_alias_match'
            : 'explicit_first_name_match',
        signals: {
          explicitlyNamed: true,
        },
        metadata: {
          matchedBy: matchedVariant ? 'name_or_alias' : null,
          matchedAlias: matchedVariant,
          aliases: getEntityAliasList(person),
          buckets: getBucketsForRole(getRelationshipRole(person)),
        },
      });
    })
    .filter(Boolean);
}

function extractMentionedGroupBuckets(message = '') {
  const lower = normalizeText(message);
  if (!lower) return [];

  const matchedBuckets = Object.entries(GROUP_BUCKET_ALIASES)
    .filter(([, aliases]) => aliases.some((alias) => messageMentionsAlias(lower, alias)))
    .map(([bucket]) => bucket);

  return [...new Set(matchedBuckets)];
}

function extractMentionedCanonicalRoles(message = '') {
  const lower = normalizeText(message);
  if (!lower) return [];

  const matchedRoles = Object.entries(ROLE_ALIASES)
    .filter(([canonicalRole, aliases]) => {
      if (messageMentionsAlias(lower, canonicalRole)) return true;
      return aliases.some((alias) => messageMentionsAlias(lower, alias));
    })
    .map(([canonicalRole]) => canonicalRole);

  return [...new Set(matchedRoles)];
}

function resolveUniqueCanonicalRoleMatch(message = '', relationships = []) {
  const mentionedRoles = extractMentionedCanonicalRoles(message);

  if (!Array.isArray(relationships) || relationships.length === 0) {
    return null;
  }

  if (mentionedRoles.length !== 1) {
    return null;
  }

  const canonicalRole = mentionedRoles[0];
  const matches = relationships.filter((person) => {
    const role = getCanonicalRole(getRelationshipRole(person));
    return role === canonicalRole;
  });

  if (matches.length !== 1) {
    return null;
  }

  const person = matches[0];

  return createEntityCandidate(person, {
    source: 'unique_canonical_role_match',
    signals: {
      explicitlyNamed: true,
      resolved: true,
      canonical: true,
    },
    metadata: {
      matchedBy: 'unique_role',
      matchedRole: canonicalRole,
      aliases: getEntityAliasList(person),
      buckets: getBucketsForRole(getRelationshipRole(person)),
      lifeState: person?.lifeState || null,
      healthState: person?.healthState || null,
      location: person?.location || null,
    },
  });
}

function buildGroupedRelationships(relationships = []) {
  return relationships.reduce((acc, person) => {
    const canonicalRole = getCanonicalRole(getRelationshipRole(person));
    if (!canonicalRole) return acc;

    if (!acc[canonicalRole]) {
      acc[canonicalRole] = [];
    }

    acc[canonicalRole].push(person);
    return acc;
  }, {});
}

function findRelationshipGroupMatches(message = '', relationships = []) {
  const lower = normalizeText(message);

  if (!lower || !Array.isArray(relationships) || relationships.length === 0) {
    return [];
  }

  const groupedRelationships = buildGroupedRelationships(relationships);
  const requestedBuckets = extractMentionedGroupBuckets(lower);

  if (requestedBuckets.length === 0) {
    return [];
  }

  return requestedBuckets
    .flatMap((bucket) => {
      const allowedRoles = GROUP_BUCKET_MEMBERS[bucket] || [];
      const members = allowedRoles.flatMap((role) => groupedRelationships[role] || []);

      if (members.length === 0) {
        return [];
      }

      const memberCandidates = members
        .map((member) =>
          createEntityCandidate(member, {
            source: 'relationship_group_member',
            metadata: {
              buckets: getBucketsForRole(getRelationshipRole(member)),
              aliases: getEntityAliasList(member),
            },
          })
        )
        .filter(Boolean);

      const groupCandidate = createEntityCandidate(
        {
          name: getGroupDisplayNameForBucket(bucket, members.length),
          role: bucket,
          relationship: bucket,
          type: 'relationship_group',
          metadata: {
            bucket,
            canonicalBucket: bucket,
            allowedRoles,
            memberCount: members.length,
            memberKeys: memberCandidates.map((candidate) => candidate.key).filter(Boolean),
          },
        },
        {
          source: 'relationship_group_match',
          signals: {
            explicitlyNamed: true,
          },
          metadata: {
            bucket,
            canonicalBucket: bucket,
            allowedRoles,
            memberCount: members.length,
            memberKeys: memberCandidates.map((candidate) => candidate.key).filter(Boolean),
            isGroupTarget: true,
          },
        }
      );

      return groupCandidate ? [groupCandidate] : [];
    })
    .filter(Boolean);
}

function hasContrastLanguage(message = '') {
  const lower = normalizeText(message);

  if (!lower) return false;

  return [
    'we are talking about',
    "we're talking about",
    'not ',
    'no ',
    'oh no',
    'that is ',
    'that was ',
    'this is ',
    'not her',
    'not him',
    'not them',
    'instead',
    'actually',
  ].some((token) => lower.includes(token));
}

function detectCorrectionPromotedKeys({
  message = '',
  explicitMatches = [],
  currentEntity = null,
} = {}) {
  const lower = normalizeText(message);

  if (!lower || !Array.isArray(explicitMatches) || explicitMatches.length === 0) {
    return [];
  }

  const hasCorrectionStyle =
    hasContrastLanguage(lower) && explicitMatches.length > 0 && currentEntity;

  if (!hasCorrectionStyle) {
    return [];
  }

  return explicitMatches
    .filter((candidate) => !sameEntity(candidate, currentEntity))
    .map((candidate) => candidate.key)
    .filter(Boolean);
}

function buildCarryForwardCandidate(currentEntity = null, isFreshOpen = false) {
  if (!currentEntity || isFreshOpen) return null;

  return createEntityCandidate(currentEntity, {
    source: currentEntity?.source || 'carry_forward',
    signals: {
      carryForward: true,
    },
  });
}

function buildPronounCandidate(pronounCandidate = null, isFreshOpen = false) {
  if (!pronounCandidate || isFreshOpen) return null;

  return createEntityCandidate(pronounCandidate, {
    source: pronounCandidate?.source || 'pronoun_resolution',
    signals: {
      pronounSupported: true,
    },
  });
}

function buildTurnAttachmentCandidate(turnAttachmentEntity = null) {
  if (!turnAttachmentEntity) return null;

  return createEntityCandidate(turnAttachmentEntity, {
    source: turnAttachmentEntity?.source || 'turn_attachment',
  });
}

function buildCandidateList({
  explicitMatches = [],
  relationshipGroupMatches = [],
  carryForwardCandidate = null,
  pronounCandidate = null,
  turnAttachmentCandidate = null,
  correctedKeys = [],
  uniqueResolvedCandidate = null,
} = {}) {
  const correctedKeySet = new Set(correctedKeys);

  const candidates = [
    uniqueResolvedCandidate,
    ...explicitMatches.map((candidate) => {
      if (!candidate?.key || !correctedKeySet.has(candidate.key)) {
        return candidate;
      }

      return createEntityCandidate(candidate, {
        source: candidate.source || 'explicit_name_match',
        signals: {
          ...(candidate.signals || {}),
          explicitlyNamed: true,
          corrected: true,
        },
        metadata: {
          ...(candidate.metadata || {}),
        },
      });
    }),
    ...relationshipGroupMatches,
    carryForwardCandidate,
    pronounCandidate,
    turnAttachmentCandidate,
  ].filter(Boolean);

  return dedupeEntities(candidates);
}

function buildNamedKeys(candidates = []) {
  if (!Array.isArray(candidates) || candidates.length === 0) {
    return [];
  }

  return candidates
    .filter((candidate) => candidate?.signals?.explicitlyNamed === true)
    .map((candidate) => candidate.key)
    .filter(Boolean);
}

function decorateRelatedEntities({
  related = [],
  named = [],
} = {}) {
  const namedKeySet = new Set((named || []).map((entity) => entity?.key).filter(Boolean));

  return sortEntitiesByScore(
    dedupeEntities(
      (related || [])
        .map((entity) => {
          if (!entity) return null;

          const isNamed = entity?.key && namedKeySet.has(entity.key);

          return createEntityCandidate(entity, {
            source: entity.source || 'entity_state_related',
            score: entity.score || 0,
            roles: entity.roles || [],
            signals: {
              ...(entity.signals || {}),
              related: true,
              explicitlyNamed: isNamed || entity?.signals?.explicitlyNamed === true,
            },
            metadata: entity.metadata || {},
          });
        })
        .filter(Boolean)
    )
  );
}

function buildEntityState({
  message = '',
  relationships = [],
  currentEntity = null,
  previousActiveEntity = null,
  candidateEntity = null,
  turnAttachmentEntity = null,
  pronounCandidateEntity = null,
  isFreshOpen = false,
} = {}) {
  const baseState = createEmptyEntityState();

  if (isFreshOpen) {
    return {
      ...baseState,
      historical: toHistoricalEntities(relationships),
    };
  }

  const explicitMatches = findExplicitRelationshipMatches(message, relationships);
  const relationshipGroupMatches = findRelationshipGroupMatches(message, relationships);
  const uniqueResolvedCandidate = resolveUniqueCanonicalRoleMatch(message, relationships);

  const carryForwardCandidate = buildCarryForwardCandidate(currentEntity, isFreshOpen);
  const pronounCandidate = buildPronounCandidate(pronounCandidateEntity, isFreshOpen);
  const attachmentCandidate =
    buildTurnAttachmentCandidate(turnAttachmentEntity || candidateEntity);

  const correctedKeys = detectCorrectionPromotedKeys({
    message,
    explicitMatches,
    currentEntity,
  });

  const candidates = buildCandidateList({
    explicitMatches,
    relationshipGroupMatches,
    carryForwardCandidate,
    pronounCandidate,
    turnAttachmentCandidate: attachmentCandidate,
    correctedKeys,
    uniqueResolvedCandidate,
  });

  if (candidates.length === 0) {
    return {
      ...baseState,
      historical: toHistoricalEntities(relationships),
    };
  }

  const namedKeys = buildNamedKeys(candidates);

  const arbitration = arbitrateEntityState({
    candidates,
    currentEntity,
    previousActiveEntity,
    namedKeys,
    historicalKeys: [],
  });

  const excluded = [
    arbitration.active,
    arbitration.previous,
    ...(arbitration.related || []),
    ...(arbitration.named || []),
  ].filter(Boolean);

  const historical = toHistoricalEntities(relationships, excluded);

  return {
    ...baseState,
    ...arbitration,
    related: decorateRelatedEntities({
      related: arbitration.related || [],
      named: arbitration.named || [],
    }),
    historical,
  };
}

function getEntityStateActive(entityState = null) {
  return entityState?.active || null;
}

function getEntityStatePrevious(entityState = null) {
  return entityState?.previous || null;
}

function getEntityStateNamed(entityState = null) {
  return Array.isArray(entityState?.named) ? entityState.named : [];
}

function getEntityStateRelated(entityState = null) {
  return Array.isArray(entityState?.related) ? entityState.related : [];
}

function getEntityStateHistorical(entityState = null) {
  return Array.isArray(entityState?.historical) ? entityState.historical : [];
}

function buildLegacyReferenceFromEntityState(entityState = null) {
  const active = getEntityStateActive(entityState);

  if (active) {
    return active;
  }

  const previous = getEntityStatePrevious(entityState);
  if (previous) {
    return previous;
  }

  const named = getEntityStateNamed(entityState);
  if (named.length > 0) {
    return named[0];
  }

  const related = getEntityStateRelated(entityState);
  if (related.length > 0) {
    return related[0];
  }

  return null;
}
export {
  buildEntityState,
  buildLegacyReferenceFromEntityState,
  getEntityStateActive,
  getEntityStatePrevious,
  getEntityStateNamed,
  getEntityStateRelated,
  getEntityStateHistorical,
  findExplicitRelationshipMatches,
  findRelationshipGroupMatches,
  detectCorrectionPromotedKeys,
  getCanonicalRole,
  getBucketsForRole,
  getBucketForAlias,
};